import { Stack, styled, SxProps, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';

import { useViewOnly } from '@/contexts/InteractionParadigm.context';
import { COLORS } from '@/styles/tokens/colors';

import { getTriggerStyling } from './styles';

export type LinkButtonProps = React.PropsWithChildren<{
  display: string;
  underline?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  sx?: SxProps;
}> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const StyledButton = styled('button')<{
  color: string;
  underline?: boolean;
  hoverColor: string;
}>(({
  theme,
  color,
  hoverColor,
  underline = true,
}: {
  color: string;
  underline?: boolean;
  hoverColor: string;
  theme: Theme;
}) => {
  const triggerStyling = getTriggerStyling(color, hoverColor, theme);

  return {
    ...triggerStyling,
    borderBottom: underline ? triggerStyling.borderBottom : 'none',
  };
});

/**
 * This is a button that looks like a link.
 */

export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(
  (
    {
      display,
      underline = true,
      startIcon,
      endIcon,
      sx = {},
      ...nativeButtonProps
    },
    ref
  ) => {
    const theme = useTheme();
    const isViewOnly = useViewOnly();
    // In presentations (view-only) keep the tenant's branded link color.
    // In dark theme contexts (e.g. the onboarding modal sidebar) use the
    // theme's light text color so the link stays legible; elsewhere use the
    // primary text color.
    let color = COLORS.TEXT.PRIMARY;
    if (isViewOnly) {
      color = theme.palette.links.main;
    } else if (theme.palette.mode === 'dark') {
      color = theme.palette.text.primary;
    }
    const hoverColor = theme.palette.links.dark;
    return (
      <StyledButton
        ref={ref}
        color={color}
        hoverColor={hoverColor}
        underline={underline}
        type="button"
        sx={{
          ...sx,
        }}
        {...nativeButtonProps}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {startIcon}
          <Typography color="inherit" variant="title-xs" component="span">
            {display}
          </Typography>
          {endIcon}
        </Stack>
      </StyledButton>
    );
  }
);

LinkButton.displayName = 'LinkButton';
